import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, ApiError, type User } from "./api.ts";
import { analytics } from "./analytics.ts";

type Session = {
  user: User | null;
  /** True until the first `me` call settles. Routes wait on this before redirecting. */
  loading: boolean;
  signedIn: (user: User) => void;
  signOut: () => Promise<void>;
};

const SessionContext = createContext<Session | null>(null);

/**
 * Who is signed in, resolved once on load.
 *
 * The session is an httpOnly cookie on `.gazar.dev`, so the app cannot read it
 * directly and has to ask the API. That means one round trip before any guarded
 * route can decide, which is why `loading` exists: redirecting to /login while
 * the answer is still in flight would bounce a signed-in user out of their own
 * app on every refresh.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api
      .me()
      .then(({ user: found }) => {
        if (cancelled) return;
        setUser(found);
        analytics.identify(found.id);
      })
      .catch((cause) => {
        // A 401 is a visitor who is not signed in, which is the normal state of
        // a public site and not an error worth surfacing.
        if (!(cause instanceof ApiError)) return;
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const signedIn = useCallback((next: User) => {
    setUser(next);
    analytics.identify(next.id);
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      // Clear locally even if the request failed: the user asked to be signed
      // out, and leaving them looking signed in is the worse failure.
      setUser(null);
    }
  }, []);

  const value = useMemo(
    () => ({ user, loading, signedIn, signOut }),
    [user, loading, signedIn, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): Session {
  const context = useContext(SessionContext);
  if (!context) throw new Error("useSession must be used inside a SessionProvider");
  return context;
}
